import { APP_INITIALIZER, ErrorHandler, NgModule, Provider } from '@angular/core';

import { authManagementService } from '@aviv-pro-navigation/authentication-core';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { appRoutingProviders, routing } from './app.routing';

import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { NgxSmartModalModule } from 'ngx-smart-modal';

import { AppComponent } from './app.component';
import { BalanceOutstandingComponent } from './balance-outstanding/balance-outstanding.component';
import { BillAssetComponent } from './bill-asset/bill-asset.component';
import { CdkTableComponent } from './components/cdk-table/cdk-table.component';
import { CreancierSelectorComponent } from './components/creancier-selector/creancier-selector.component';
import { CreditCardsComponent } from './components/credit-cards/credit-cards.component';
import { FastPickerComponent } from './components/fast-picker/fast-picker.component';
import { FiltreRechercheComponent } from './components/filters/filters.component';
import { FooterComponent } from './components/footer/footer.component';
import { ControlMessagesComponent } from './components/form/form-control-error-msg.component';
import { PaginationComponent } from './components/ui/pagination/pagination.component';
import { ExtractAccountComponent } from './extract-account/extract-account.component';
import { HomeComponent } from './home/home.component';
import { InfosContactComponent } from './infos-contact/infos-contact.component';
import { PaymentCardsComponent } from './payment-cards/payment-cards.component';
import { PaymentMeansComponent } from './payment-means/payment-means.component';
import { PurchaseOrdersSignIframeComponent } from './purchase-orders-sign-iframe/purchase-orders-sign-iframe.component';
import { PurchaseOrdersSignComponent } from './purchase-orders-sign/purchase-orders-sign.component';
import { SepaMandateFormComponent } from './sepa-mandate-form/sepa-mandate-form.component';

import { ApplyAccessRightsDirective } from './ApplyAccessRights.directive';
import { ApplyWriteRightsDirective } from './ApplyWriteRights.directive';
import { SvgDirective } from './directives/svg.directive';
import { TooltipDirective } from './tooltip.directive';

import { GoogleTagManagerService, trackGAinit } from './googleTagManager/googleTagManager.service';
import { CheckAccessService } from './services/checkAccess.service';
import { CreditCardsService } from './services/credit-cards.service';
import { DeactivateGuardService } from './services/deactivateGuardService.service';
import { FournisseurService } from './services/fournisseur.service';
import { MailService } from './services/mail.service';
import { PanierService } from './services/panier.service';
import { RightsService } from './services/rights.service';
import { RouteAccessService } from './services/routeAccess.service';
import { SignatureService } from './services/signature.service';
import { ValidationFormService } from './services/validationForm.service';

import { ErrorModule } from './error/error.module';

import { CookieService } from 'ngx-cookie-service';

import { LOCALE_ID } from '@angular/core';

import * as moment from 'moment';

import * as utils from './utils/utils';

import { ReferenceService } from './services/reference.service';
import { WindowService } from './services/window.service';

import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';

import { SidebarComponent } from './components/sidebar/sidebar.component';
import { TabBarComponent } from './components/tab-bar/tab-bar.component';
import { NavigationService } from './services/navigation.service';

import { CdkTableModule } from '@angular/cdk/table';
import { MyselogerproWebComponentsModule } from 'myselogerpro-web-components/myselogerpro-web-components.module';
import { AppLayoutComponent } from './app-layout/app-layout.component';
import { DateFormat } from './common/date-format';
import { MessageCardComponent } from './components/message-card/message-card.component';
import { TabBarOfflineComponent } from './components/tab-bar-offline/tab-bar-offline.component';
import { LoaderComponent } from './components/ui/loader/loader.component';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { MonitoringInterceptor } from './interceptors/monitoring.interceptor';
import { PaymentIframeComponent } from './payment-iframe/payment-iframe.component';
import { TruncatePipe } from './pipes/truncate.pipe';
import { GlobalErrorHandlerService } from './services/global-error-handler.service';
import { UserService } from './services/user.service';

registerLocaleData(localeFr, 'fr');

export const INIT_CONFIGURATION: Provider = {
  provide: APP_INITIALIZER,
  useFactory: initConfiguration,
  deps: [ReferenceService],
  multi: true,
};


@NgModule({
    declarations: [
        AppComponent,
        HomeComponent,
        InfosContactComponent,
        PaginationComponent,
        BillAssetComponent,
        BalanceOutstandingComponent,
        FiltreRechercheComponent,
        ExtractAccountComponent,
        FooterComponent,
        FastPickerComponent,
        TooltipDirective,
        PaymentMeansComponent,
        PaymentCardsComponent,
        ControlMessagesComponent,
        SepaMandateFormComponent,
        ApplyAccessRightsDirective,
        ApplyWriteRightsDirective,
        PurchaseOrdersSignComponent,
        PurchaseOrdersSignIframeComponent,
        SidebarComponent,
        TabBarComponent,
        SvgDirective,
        CreditCardsComponent,
        TabBarOfflineComponent,
        CdkTableComponent,
        TruncatePipe,
        CreancierSelectorComponent,
        DateFormat,
        MessageCardComponent,
        LoaderComponent,
        AppLayoutComponent,
        PaymentIframeComponent
    ],
    imports: [
        BrowserModule,
        ReactiveFormsModule,
        FormsModule,
        HttpClientModule,
        routing,
        ErrorModule,
        BrowserAnimationsModule,
        FontAwesomeModule,
        CdkTableModule,
        MatDatepickerModule,
        MatInputModule,
        MatFormFieldModule,
        MatNativeDateModule,
        MatIconModule,
        ToastrModule.forRoot(),
        MatRadioModule,
        NgxSmartModalModule.forRoot(),
        MyselogerproWebComponentsModule
    ],
    providers: [
        CookieService,
        FournisseurService,
        appRoutingProviders,
        Title,
        PanierService,
        RouteAccessService,
        RightsService,
        CheckAccessService,
        {
            provide: LOCALE_ID,
            useValue: 'fr'
        },
        MailService,
        ValidationFormService,
        ReferenceService,
        UserService,
        WindowService,
        DeactivateGuardService,
        SignatureService,
        GoogleTagManagerService,
        NavigationService,
        CreditCardsService,
        {
            provide: APP_INITIALIZER,
            useFactory: trackGAinit,
            multi: true,
            deps: [GoogleTagManagerService]
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MonitoringInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandlerService
        },
        INIT_CONFIGURATION
    ],
    bootstrap: [AppComponent]
})

export class AppModule {

    constructor() {
        moment.locale('fr');
    }

}


export function initConfiguration(referenceService: ReferenceService) {
  return async () => {
        const baseUrl = referenceService.staticReferences.ApiDuoServer + referenceService.staticReferences.ApiDuoVersion;

        // Init shared auth service
        await authManagementService.init(baseUrl);
        return true;
  };
}
